import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import HelportLogo from './components/HelportLogo';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { getRegisterAddedBy, isAdminUser, canManageEmployeeActions, canViewAccountLogs, isSeniorTeamLeaderUser, isEmployeePortalUser } from './utils/dbHelpers';
import { speakAttendance, preloadSpeechVoices, cancelSpeech } from './utils/voiceHelpers';
import { parseQrAttendancePayload, findEmployeeByUid, normalizeEmployeeId } from './utils/qrHelpers';
import { createScanGate, formatScanWaitMessage, findConfirmedActiveLog } from './utils/scanCooldown';
import { ACCOUNT_DREAME, ACCOUNT_MEGELIN, normalizeTlAccount, canViewAccountScope, ACCOUNT_ALL } from './constants/accountOptions';
import {
  filterAttendanceLogs,
  buildAttendanceExportRows,
  formatExportDateRange,
  getEmployeeAccount,
} from './utils/attendanceLogHelpers';
import DashboardBackground from './components/DashboardBackground';

import LoginScanner from './components/LoginScanner';
import AdminDashboard from './components/AdminDashboard';
import RegisterEmployee from './components/RegisterEmployee';
import AttendanceLogs from './components/AttendanceLogs';
import EmployeeList from './components/EmployeeList';
import LandingPage from './components/LandingPage';
import UserManagement from './components/UserManagement';
import TLAccounts from './components/TLAccounts';
import EmployeePortalAccounts from './components/EmployeePortalAccounts';
import EmployeeProfile from './components/EmployeeProfile';
import API_URL, { parseJsonResponse, resolveWorkingApiUrl } from './api';

function App() {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [user, setUser] = useState(null);
  const [isAdminPortal, setIsAdminPortal] = useState(false);
  const [activeTab, setActiveTab] = useState('monitor'); 
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [inputValue, setInputValue] = useState("");
  
  const [scannedUser, setScannedUser] = useState(null);
  const [adminCredential, setAdminCredential] = useState("");
  const [adminPass, setAdminPass] = useState("");
  const [allLogs, setAllLogs] = useState([]);
  const [registeredEmployees, setRegisteredEmployees] = useState([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [newEmpId, setNewEmpId] = useState("");
  const [newEmpEID, setNewEmpEID] = useState(""); 
  const [newEmpName, setNewEmpName] = useState("");
  const [newEmpPosition, setNewEmpPosition] = useState("");
  const [newEmpDateHired, setNewEmpDateHired] = useState(""); 
  const [newEmpShift, setNewEmpShift] = useState(""); 
  const [newEmpAccount, setNewEmpAccount] = useState(ACCOUNT_DREAME);
  const [photoBase64, setPhotoBase64] = useState("");

  const [searchDept, setSearchDept] = useState("");
  const [filterShift, setFilterShift] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editPosition, setEditPosition] = useState("");
  const [editShift, setEditShift] = useState(""); 
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [apiStatus, setApiStatus] = useState({ checked: false, ok: true, legacy: false, message: '' });
  const [activeApiUrl, setActiveApiUrl] = useState(API_URL);
  const [showEmployeeProfile, setShowEmployeeProfile] = useState(false);
  const [guestMonitor, setGuestMonitor] = useState(false);

  const videoRef = useRef(null);
  const fileInputRef = useRef(null); 
  const prevLogsRef = useRef([]);
  const scanGateRef = useRef(createScanGate());
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  useEffect(() => {
    preloadSpeechVoices();
    const onVoicesChanged = () => preloadSpeechVoices();
    window.speechSynthesis?.addEventListener('voiceschanged', onVoicesChanged);
    return () => window.speechSynthesis?.removeEventListener('voiceschanged', onVoicesChanged);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 768) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleNavTab = (tab) => {
    setActiveTab(tab);
    setMobileMenuOpen(false);
  };

  const captureSnapshot = useCallback(async (employeeName) => {
    if (videoRef.current && videoRef.current.readyState >= 2) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth || 640;
        canvas.height = videoRef.current.videoHeight || 480;
        const ctx = canvas.getContext('2d');
        
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

        // Send plain image to backend - backend will add timestamp
        const imageData = canvas.toDataURL('image/png');

        const payload = {
          action: 'save_snapshot',
          name: employeeName,
          image: imageData
        };

        await fetch(activeApiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        console.log("Snapshot successfully sent to PHP!");
      } catch (err) {
        console.error("Auto-save failed:", err);
      }
    }
  }, [activeApiUrl]);

  const getDailyAnalytics = () => {
    const current = new Date();
    const dayOfWeek = current.getDay();
    const monday = new Date(current);
    const diff = current.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    monday.setDate(diff);

    return [...Array(5)].map((_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const dateStr = d.toISOString().split('T')[0];
      const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
      const count = allLogs.filter(log => log.date === dateStr).length;
      return { date: dateStr, dayName, count };
    });
  };

  const playAttendanceVoice = useCallback((fullName, action = 'login') => {
    speakAttendance(fullName, action);
  }, []);

  const getScopedWorkforceData = useCallback(() => {
    if (isEmployeePortalUser(user)) {
      const self = registeredEmployees.find(
        (emp) => normalizeEmployeeId(emp.employeeId) === normalizeEmployeeId(user?.employeeId)
      );
      return { employees: self ? [self] : [], logs: allLogs };
    }

    if (isAdminUser(user)) {
      return { employees: registeredEmployees, logs: allLogs };
    }

    const tlAccountScope = normalizeTlAccount(user?.account);
    const hasAllAccountAccess = tlAccountScope === ACCOUNT_ALL;

    const canViewEmployeeRecord = (emp) => {
      const empAccount = getEmployeeAccount(emp);
      if (isSeniorTeamLeaderUser(user)) {
        return empAccount === ACCOUNT_MEGELIN || canViewAccountScope(user.account, empAccount);
      }
      if (hasAllAccountAccess) {
        return empAccount === ACCOUNT_DREAME || empAccount === ACCOUNT_MEGELIN;
      }
      return canViewAccountScope(user.account, empAccount);
    };

    const usesBroadEmployeeScope = isSeniorTeamLeaderUser(user) || hasAllAccountAccess;

    const ownedEmployees = usesBroadEmployeeScope
      ? registeredEmployees.filter(canViewEmployeeRecord)
      : registeredEmployees.filter((emp) => (
          (emp.added_by === user?.username || emp.added_by === user?.name) && canViewEmployeeRecord(emp)
        ));

    const ownedIds = new Set(ownedEmployees.map((emp) => normalizeEmployeeId(emp.employeeId)));
    const logs = allLogs.filter((log) => ownedIds.has(normalizeEmployeeId(log.employeeId)));

    return { employees: ownedEmployees, logs };
  }, [user, registeredEmployees, allLogs]);

  const exportToPDF = (account = ACCOUNT_DREAME) => {
    const { employees, logs } = getScopedWorkforceData();
    const accountFilter = account || null;
    const reportLabel = accountFilter || 'My';
    const filteredLogs = filterAttendanceLogs(logs, employees, {
      startDate,
      endDate,
      account: accountFilter,
    });
    const rows = buildAttendanceExportRows(filteredLogs, employees);

    if (rows.length === 0) {
      alert('No records to export for the selected filters.');
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const generatedAt = new Date().toLocaleString('en-PH', { hour12: true });
    const dateRange = formatExportDateRange(startDate, endDate);

    doc.setFillColor(4, 16, 24);
    doc.rect(0, 0, pageWidth, 34, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.text('HELPORT RFID WORKFORCE SYSTEM', 14, 14);
    doc.setFontSize(11);
    doc.text(`${reportLabel} Attendance Report`, 14, 22);
    doc.setTextColor(180, 255, 240);
    doc.setFontSize(9);
    doc.text(`Date Range: ${dateRange}`, 14, 29);
    doc.text(`Generated: ${generatedAt}`, pageWidth - 14, 29, { align: 'right' });

    autoTable(doc, {
      startY: 40,
      head: [['No.', 'Name', 'UID', 'Position', 'Date', 'Time In', 'Time Out', 'Remarks', 'Status']],
      body: rows.map((row, index) => [
        index + 1,
        row.name,
        row.uid,
        row.position,
        row.date,
        row.timeIn,
        row.timeOut,
        row.remarks,
        row.status,
      ]),
      theme: 'grid',
      styles: {
        fontSize: 8.5,
        cellPadding: 2.8,
        textColor: [20, 30, 40],
        lineColor: [210, 220, 228],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: [0, 191, 134],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
      },
      alternateRowStyles: { fillColor: [245, 250, 248] },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 42 },
        2: { cellWidth: 24 },
        3: { cellWidth: 38 },
        4: { cellWidth: 24, halign: 'center' },
        5: { cellWidth: 22, halign: 'center' },
        6: { cellWidth: 22, halign: 'center' },
        7: { cellWidth: 28, halign: 'center' },
        8: { cellWidth: 22, halign: 'center' },
      },
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(120, 130, 140);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          pageWidth - 14,
          doc.internal.pageSize.getHeight() - 8,
          { align: 'right' }
        );
      },
    });

    const stamp = new Date().toISOString().slice(0, 10);
    doc.save(`${reportLabel}_Attendance_Report_${stamp}.pdf`);
  };

  const exportToExcel = (account = ACCOUNT_DREAME) => {
    const { employees, logs } = getScopedWorkforceData();
    const accountFilter = account || null;
    const reportLabel = accountFilter || 'My';
    const filteredLogs = filterAttendanceLogs(logs, employees, {
      startDate,
      endDate,
      account: accountFilter,
    });
    const rows = buildAttendanceExportRows(filteredLogs, employees);

    if (rows.length === 0) {
      alert('No records to export for the selected filters.');
      return;
    }

    const generatedAt = new Date().toLocaleString('en-PH', { hour12: true });
    const dateRange = formatExportDateRange(startDate, endDate);
    const headerRows = [
      ['HELPORT RFID WORKFORCE SYSTEM'],
      [`${reportLabel} Attendance Report`],
      [`Date Range: ${dateRange}`],
      [`Generated: ${generatedAt}`],
      [],
      ['No.', 'Name', 'UID', 'Position', 'Date', 'Time In', 'Time Out', 'Remarks', 'Status'],
    ];
    const dataRows = rows.map((row, index) => [
      index + 1,
      row.name,
      row.uid,
      row.position,
      row.date,
      row.timeIn,
      row.timeOut,
      row.remarks,
      row.status,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([...headerRows, ...dataRows]);
    worksheet['!cols'] = [
      { wch: 6 },
      { wch: 28 },
      { wch: 14 },
      { wch: 24 },
      { wch: 14 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
      { wch: 12 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `${reportLabel} Logs`);
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `${reportLabel}_Attendance_Report_${stamp}.xlsx`);
  };

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (startDate && endDate) {
        params.set('from', startDate);
        params.set('to', endDate);
      }
      if (isEmployeePortalUser(user) && user?.employeeId) {
        params.set('employee_id', user.employeeId);
      }
      const query = params.toString();
      const url = query ? `${activeApiUrl}?${query}` : activeApiUrl;

      const response = await fetch(url);
      const data = await parseJsonResponse(response);
      if (!data || !Array.isArray(data.logs)) {
        return;
      }
      const sortedLogs = data.logs.sort((a, b) => b.id - a.id);

      if (prevLogsRef.current.length > 0) {
        let changedLog = null;

        for (let i = 0; i < sortedLogs.length; i++) {
            const newLog = sortedLogs[i];
            const oldLog = prevLogsRef.current.find(l => l.id === newLog.id);
            
            if (!oldLog) {
                changedLog = newLog;
                break;
            }
            else if (oldLog.timeOut !== newLog.timeOut) {
                changedLog = newLog;
                break;
            }
        }

        if (changedLog && !scannedUser && activeTabRef.current === 'monitor') {
            const emp = findEmployeeByUid(data.employees || [], changedLog.employeeId);
            if (emp) {
                setScannedUser(emp);
                const isTimeOut = changedLog.timeOut && changedLog.timeOut !== '--:--' && changedLog.timeOut !== '';
                playAttendanceVoice(emp.name, isTimeOut ? 'logout' : 'login');
                
                captureSnapshot(emp.name);
                
                setTimeout(() => setScannedUser(null), 4000);
            }
        }
      }

      prevLogsRef.current = sortedLogs;
      setAllLogs(sortedLogs);
      setRegisteredEmployees(data.employees || []);
    } catch (err) { 
      console.error("API Error:", err);
    }
  }, [startDate, endDate, scannedUser, playAttendanceVoice, captureSnapshot, user, activeApiUrl]);

  useEffect(() => {
    let cancelled = false;
    resolveWorkingApiUrl().then((result) => {
      if (cancelled) return;
      if (result.url) {
        setActiveApiUrl(result.url);
      }
      setApiStatus({
        checked: true,
        ok: result.ok,
        legacy: Boolean(result.legacy),
        message: result.ok ? '' : (result.message || ''),
      });
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      if (!startDate && !endDate) {
        fetchData();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchData, startDate, endDate]);

  useEffect(() => {
    let currentStream = null;
    let isMounted = true;

    const startCamera = async () => {
      if (activeTab === 'monitor' || !user) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          
          if (!isMounted) {
            stream.getTracks().forEach(track => track.stop());
            return;
          }
          
          currentStream = stream;
          
          const attachToVideo = () => {
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
              videoRef.current.play().catch(e => console.log("Play error:", e));
            } else if (isMounted) {
              setTimeout(attachToVideo, 200); 
            }
          };
          
          attachToVideo();

        } catch (err) {
          console.error("Camera Error:", err);
        }
      }
    };

    startCamera();

    return () => {
      isMounted = false;
      if (currentStream) {
        currentStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [activeTab, user]); 

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPhotoBase64(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRegisterEmployee = async (e) => {
    e.preventDefault();
    const id = newEmpId.trim().toUpperCase();
    const name = newEmpName.trim();
    
    if (!id || !name || !photoBase64 || !newEmpPosition || !newEmpEID || !newEmpShift || !newEmpDateHired) {
        setError("Please complete all fields.");
        setTimeout(() => setError(""), 2000);
        return;
    }

    try {
      const payload = {
        action: 'register',
        employeeId: id,
        eid: newEmpEID,
        name: name,
        position: newEmpPosition,
        shift: newEmpShift,
        date_hired: newEmpDateHired,
        photo: photoBase64,
        status: 'INACTIVE',
        added_by: getRegisterAddedBy(user),
        account: newEmpAccount,
      };
      const res = await fetch(activeApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonResponse(res);
      if (data?.status === 'success') {
        setSuccessMsg("Employee Registered Successfully!");
        setError("");
        setNewEmpId(""); setNewEmpEID(""); setNewEmpName(""); setNewEmpPosition(""); setNewEmpShift(""); setNewEmpAccount(ACCOUNT_DREAME); setPhotoBase64(""); setNewEmpDateHired("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        fetchData();
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        setError(data.message || "Registration failed");
        setTimeout(() => setError(""), 2000);
      }
    } catch (err) {
      setError("Database Error");
    }
  };

  const handleAttendance = useCallback(async (eOrUid) => {
    if (eOrUid?.preventDefault) eOrUid.preventDefault();

    const id = parseQrAttendancePayload(typeof eOrUid === 'string' ? eOrUid : inputValue);
    if (!id) return;

    const employee = findEmployeeByUid(registeredEmployees, id);
    if (!employee) {
      setError('INVALID UID: NOT REGISTERED');
      setTimeout(() => setError(''), 2000);
      setInputValue('');
      return;
    }

    const employeeUid = normalizeEmployeeId(employee.employeeId);
    const gate = scanGateRef.current.check(employeeUid);
    if (!gate.allowed) {
      if (gate.reason === 'cooldown') {
        setError(formatScanWaitMessage(gate.remainingMs));
        setTimeout(() => setError(''), 2500);
      } else if (gate.reason === 'processing') {
        setError('Scan in progress. Please wait...');
        setTimeout(() => setError(''), 2000);
      }
      setInputValue('');
      return;
    }

    scanGateRef.current.begin(gate.key);

    const activeRecord = findConfirmedActiveLog(allLogs, employeeUid);
    const action = activeRecord ? 'logout' : 'login';
    setInputValue('');

    try {
      const res = await fetch(activeApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, employeeId: employeeUid, position: employee.position }),
      });
      const data = await parseJsonResponse(res);

      if (data?.status === 'success') {
        await fetchData();

        if (data.message === 'scan_cooldown' || data.message === 'already_active') {
          scanGateRef.current.complete(gate.key);
          return;
        }

        const recordedAction = data.message === 'logout' ? 'logout' : 'login';
        speakAttendance(employee.name, recordedAction);
        setScannedUser({ ...employee, scanAction: recordedAction });
        captureSnapshot(employee.name);
        scanGateRef.current.complete(gate.key);
        setTimeout(() => setScannedUser(null), 4000);
        return;
      }

      setError(data?.message || 'Scan could not be recorded.');
      setTimeout(() => setError(''), 2500);
      await fetchData();
      scanGateRef.current.fail(gate.key);
    } catch (err) {
      setError('Database Error');
      setTimeout(() => setError(''), 2500);
      scanGateRef.current.fail(gate.key);
    }
  }, [inputValue, registeredEmployees, allLogs, captureSnapshot, fetchData, activeApiUrl]);

  const handleDeleteEmployee = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      try {
        await fetch(activeApiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'delete', employeeId: id })
        });
        fetchData();
      } catch (err) { alert("Server connection error"); }
    }
  };

  const startEdit = (emp) => {
    setEditingId(emp.employeeId);
    setEditName(emp.name || '');
    setEditPosition(emp.position || '');
    setEditShift(emp.shift || '08:00-17:00');
  };

  const handleUpdateEmployee = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const originalEmp = registeredEmployees.find(emp => emp.employeeId === editingId);
    if (!originalEmp) return;
    const name = editName.trim();
    if (!name || !editPosition) {
      alert('Please enter name and position.');
      return;
    }
    try {
      const res = await fetch(activeApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'register',
          employeeId: editingId,
          eid: originalEmp.eid,
          name,
          position: editPosition,
          shift: editShift,
          status: originalEmp.status,
          date_hired: originalEmp.date_hired,
          photo: originalEmp.photo,
          user_role: user?.role || 'admin',
          added_by: originalEmp.added_by || 'admin',
          account: originalEmp.account || ACCOUNT_DREAME,
        })
      });
      const data = await parseJsonResponse(res);
      if (data?.status === 'success') {
        setEditingId(null);
        fetchData();
        alert("Employee updated successfully!");
      } else {
        alert(data.message || "Update failed");
      }
    } catch (err) { alert("Update failed"); }
  };

  const handleAdminAuth = async (e) => {
    e.preventDefault();
    
    if ((adminCredential === "admin" || adminCredential === "ricmascarinas@gmail.com") && adminPass === "ricmascarinas") {
      setUser({ name: "Ric Mascarinas", role: "admin" }); 
      setActiveTab('monitor'); 
      setAdminCredential("");
      setAdminPass("");
      return; 
    } 

        if ((adminCredential === "admin" || adminCredential === "tian.lu@HELPORT.AI") && adminPass === "lutian") {
      setUser({ name: "Lu Tian", role: "admin" }); 
      setActiveTab('monitor'); 
      setAdminCredential("");
      setAdminPass("");
      return; 
    } 

        if ((adminCredential === "admin" || adminCredential === "BRUCE.HAN@HELPORT.AI") && adminPass === "brucehan") {
      setUser({ name: "Bruce Han", role: "admin" }); 
      setActiveTab('monitor'); 
      setAdminCredential("");
      setAdminPass("");
      return; 
    }
    
        if ((adminCredential === "admin" || adminCredential === "helportphadmin@HELPORT.AI") && adminPass === "rechelle") {
      setUser({ name: "Rechelle Ocino", role: "admin" });
      setActiveTab('monitor'); 
      setAdminCredential("");
      setAdminPass("");
      return; 
    } 

       if ((adminCredential === "admin" || adminCredential === "russellcelerio@helport.ai") && adminPass === "russell") {
      setUser({ name: "Russell Celerio", role: "admin" });
      setActiveTab('monitor'); 
      setAdminCredential("");
      setAdminPass("");
      return; 
    }
    
           if ((adminCredential === "admin" || adminCredential === "leogilpavo@helport.ai") && adminPass === "leogilpavo") {
      setUser({ name: "Leogil Pavo", role: "admin" });
      setActiveTab('monitor'); 
      setAdminCredential("");
      setAdminPass("");
      return; 
    } 

    
    
    try {
      const response = await fetch(activeApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'login',
          username: String(adminCredential || '').trim(),
          password: adminPass,
        }),
      });

      const loginData = await parseJsonResponse(response);

      if (!loginData) {
        setError('Walang valid response mula sa server. I-upload ang latest api.php sa helportattendance.com.');
        setTimeout(() => setError(''), 3000);
        return;
      }

      if (loginData.status === 'success') {
        const isEmployee = String(loginData.role || '').toLowerCase() === 'employee';

        if (isEmployee) {
          const employeeUser = {
            name: loginData.name,
            username: loginData.username,
            role: 'employee',
            employeeId: loginData.employeeId,
            portalAccountId: loginData.portalAccountId,
            account: loginData.account || ACCOUNT_DREAME,
          };

          try {
            const params = new URLSearchParams({ employee_id: String(employeeUser.employeeId) });
            const sessionRes = await fetch(`${activeApiUrl}?${params.toString()}`);
            const sessionData = await parseJsonResponse(sessionRes);
            if (Array.isArray(sessionData?.logs)) {
              setAllLogs(sessionData.logs.sort((a, b) => b.id - a.id));
            }
            if (Array.isArray(sessionData?.employees)) {
              setRegisteredEmployees(sessionData.employees);
            }
          } catch (sessionErr) {
            console.warn('Employee profile preload failed:', sessionErr);
          }

          setUser(employeeUser);
          setActiveTab('my-logs');
          setAdminCredential('');
          setAdminPass('');
          return;
        }

        let tlAccount = normalizeTlAccount(loginData.account);

        try {
          const tlRes = await fetch(`${activeApiUrl}?action=get_tls`);
          const tlData = await parseJsonResponse(tlRes);
          const matchedTl = (tlData?.tls || []).find((tl) => tl.username === loginData.username);
          if (matchedTl?.account) {
            tlAccount = normalizeTlAccount(matchedTl.account);
          }
        } catch (lookupErr) {
          console.warn('TL account lookup failed:', lookupErr);
        }

        setUser({
          name: loginData.name,
          username: loginData.username,
          role: loginData.role,
          account: tlAccount,
        });
        setActiveTab('monitor');
        setAdminCredential('');
        setAdminPass('');
      } else {
        setError(loginData.message || 'Invalid Username or Password!');
        setTimeout(() => setError(''), 2000);
      }
    } catch (err) {
      console.error("Login Error:", err);
      setError("Database Connection Error!");
      setTimeout(() => setError(""), 2000);
    }
  };

  const handleEmployeePortalLogout = () => {
    cancelSpeech();
    prevLogsRef.current = [];
    setUser(null);
  };

  const { employees: displayedEmployees, logs: logsForUser } = getScopedWorkforceData();
  const showDreameLogs = canViewAccountLogs(user, ACCOUNT_DREAME);
  const showMegelinLogs = canViewAccountLogs(user, ACCOUNT_MEGELIN);

  useEffect(() => {
    if (!user || isAdminUser(user)) return;
    if (activeTab === 'dreame-logs' && !showDreameLogs) {
      setActiveTab(showMegelinLogs ? 'megelin-logs' : 'dashboard');
    }
    if (activeTab === 'megelin-logs' && !showMegelinLogs) {
      setActiveTab(showDreameLogs ? 'dreame-logs' : 'dashboard');
    }
  }, [user, activeTab, showDreameLogs, showMegelinLogs]);

  if (!user) {
    if (guestMonitor) {
      return (
        <div className="fullscreen-live-wrapper">
          <LoginScanner
            videoRef={videoRef}
            allLogs={allLogs}
            registeredEmployees={registeredEmployees}
            scannedUser={scannedUser}
            error={error}
            inputValue={inputValue}
            setInputValue={setInputValue}
            handleAttendance={handleAttendance}
            setIsAdminPortal={setIsAdminPortal}
            isAdminPortal={isAdminPortal}
            handleAdminAuth={handleAdminAuth}
            adminCredential={adminCredential}
            setAdminCredential={setAdminCredential}
            adminPass={adminPass}
            setAdminPass={setAdminPass}
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
            isFullscreen={true}
            onBackToDashboard={() => setGuestMonitor(false)}
          />
        </div>
      );
    }

    return (
      <LandingPage
        handleAdminAuth={handleAdminAuth}
        adminCredential={adminCredential}
        setAdminCredential={setAdminCredential}
        adminPass={adminPass}
        setAdminPass={setAdminPass}
        onOpenTerminal={() => setGuestMonitor(true)}
      />
    );
  }

  if (isEmployeePortalUser(user)) {
    const { employees: employeeProfile, logs: employeeLogs } = getScopedWorkforceData();
    const employeeRecord = employeeProfile[0] || null;

    return (
      <div className="app-container employee-portal-shell">
        <nav className="top-nav employee-portal-nav">
          <div className="nav-brand">
            <HelportLogo variant="nav" showTagline onDark={isDarkMode} />
          </div>
          <div className="nav-info employee-portal-nav-actions">
            <button
              type="button"
              className="employee-theme-toggle"
              onClick={() => setIsDarkMode(!isDarkMode)}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDarkMode ? 'Light mode' : 'Dark mode'}
            >
              {isDarkMode ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" />
                  <line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>
            <button
              type="button"
              className="nav-profile-chip nav-profile-chip--portal"
              title="View my profile"
              onClick={() => setShowEmployeeProfile(true)}
            >
              <span className="nav-profile-chip-avatar">
                <img
                  src={employeeRecord?.photo || 'https://via.placeholder.com/64?text=User'}
                  alt=""
                  onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/64?text=User'; }}
                />
              </span>
              <span className="nav-profile-chip-copy">
                <span className="nav-profile-chip-name">{user?.name || 'Employee'}</span>
                <span className="nav-profile-chip-meta">View Profile</span>
              </span>
            </button>
            <button type="button" className="logout-btn" onClick={handleEmployeePortalLogout}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span className="logout-label">Logout</span>
            </button>
          </div>
        </nav>

        <div className="main-layout employee-portal-layout">
          <main className="content-area content-area--full">
            <div className="admin-bg-layer">
              <DashboardBackground />
            </div>
            <div className="admin-content-wrapper employee-portal-content">
              <EmployeeProfile
                user={user}
                employee={employeeRecord}
                apiUrl={activeApiUrl}
                onPhotoUpdated={fetchData}
                open={showEmployeeProfile}
                onClose={() => setShowEmployeeProfile(false)}
              />
              <AttendanceLogs
                account={ACCOUNT_DREAME}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                exportToPDF={exportToPDF}
                exportToExcel={exportToExcel}
                allLogs={employeeLogs}
                registeredEmployees={employeeProfile}
                personalMode
                personalName={user?.name}
              />
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (activeTab === 'monitor') {
    return (
      <div className="fullscreen-live-wrapper">
        <LoginScanner 
          videoRef={videoRef} allLogs={allLogs} registeredEmployees={registeredEmployees}
          scannedUser={scannedUser} error={error} inputValue={inputValue} setInputValue={setInputValue}
          handleAttendance={handleAttendance} setIsAdminPortal={setIsAdminPortal} isAdminPortal={isAdminPortal}
          handleAdminAuth={handleAdminAuth} adminCredential={adminCredential} setAdminCredential={setAdminCredential}
          adminPass={adminPass} setAdminPass={setAdminPass} isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode}
          isFullscreen={true}
          onBackToDashboard={() => setActiveTab('dashboard')}
        />
      </div>
    );
  }

  return (
    <div className="app-container">
      <nav className="top-nav">
        <div className="nav-brand">
          <button
            type="button"
            className={`nav-menu-toggle ${mobileMenuOpen ? 'nav-menu-toggle--open' : ''}`}
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            <span />
            <span />
            <span />
          </button>
          <HelportLogo variant="nav" showTagline onDark={isDarkMode} />
        </div>
        <div className="nav-info">
          <div className="nav-status-group">
            <button type="button" className="on-duty-btn" onClick={() => handleNavTab('monitor')}>
              <span className="status-dot dot-active nav-pulse-dot" />
              <span className="on-duty-label-full">ON-DUTY: {allLogs.filter(l => !l.timeOut || l.timeOut === '' || l.timeOut === '--:--').length}</span>
              <span className="on-duty-label-short">ON: {allLogs.filter(l => !l.timeOut || l.timeOut === '' || l.timeOut === '--:--').length}</span>
            </button>
            <span className="nav-status-divider" aria-hidden="true" />
            <button
              type="button"
              className={`role-badge ${user?.role === 'admin' ? 'admin' : 'tl'}`}
              onClick={() => handleNavTab('dashboard')}
              title={user?.role === 'admin' ? 'Admin' : user?.name}
            >
              {user?.role === 'admin' ? 'ADMIN' : (user?.name || 'Team Leader')}
            </button>
          </div>
          <button type="button" className="logout-btn" onClick={() => setUser(null)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span className="logout-label">Logout</span>
          </button>
        </div>
      </nav>
      
      <div className="main-layout">
        {mobileMenuOpen && (
          <button
            type="button"
            className="sidebar-backdrop"
            aria-label="Close menu"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
        <aside className={`sidebar ${mobileMenuOpen ? 'sidebar--open' : ''}`}>
          <button className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => handleNavTab('dashboard')}>Dashboard</button>
          <button className={`nav-item ${activeTab === 'monitor' ? 'active' : ''}`} onClick={() => handleNavTab('monitor')}>Live View</button>
          {showDreameLogs && (
            <button className={`nav-item ${activeTab === 'dreame-logs' ? 'active' : ''}`} onClick={() => handleNavTab('dreame-logs')}>DREAME Logs</button>
          )}
          {showMegelinLogs && (
            <button className={`nav-item ${activeTab === 'megelin-logs' ? 'active' : ''}`} onClick={() => handleNavTab('megelin-logs')}>MEGELIN Logs</button>
          )}
          
          <button className={`nav-item ${activeTab === 'register' ? 'active' : ''}`} onClick={() => handleNavTab('register')}>
            {user?.role === 'admin' ? 'Register Employee' : 'Register Agent'}
          </button>
          
          <button className={`nav-item ${activeTab === 'employees' ? 'active' : ''}`} onClick={() => handleNavTab('employees')}>
            {user?.role === 'admin' ? 'Employee List' : 'Agent List'}
          </button>
          
          {user?.role === 'admin' && (
            <>
              <button className={`nav-item ${activeTab === 'user-management' ? 'active' : ''}`} onClick={() => handleNavTab('user-management')}>Register TL</button>
              <button className={`nav-item ${activeTab === 'tl-accounts' ? 'active' : ''}`} onClick={() => handleNavTab('tl-accounts')}>TL Accounts</button>
              <button className={`nav-item ${activeTab === 'employee-portal-accounts' ? 'active' : ''}`} onClick={() => handleNavTab('employee-portal-accounts')}>Employee Accounts</button>
            </>
          )}
        </aside>

        <main className="content-area">
          <div className="admin-bg-layer">
            <DashboardBackground />
          </div>

          <div className="admin-content-wrapper">
            {apiStatus.checked && !apiStatus.ok && activeTab !== 'employee-portal-accounts' && (
              <div className="api-status-banner" role="alert">
                <strong>API setup:</strong> {apiStatus.message}
              </div>
            )}
            
            {activeTab === 'dashboard' && <AdminDashboard allLogs={allLogs} registeredEmployees={registeredEmployees} getDailyAnalytics={getDailyAnalytics} />}
            
            {activeTab === 'dreame-logs' && showDreameLogs && (
              <AttendanceLogs 
                account={ACCOUNT_DREAME}
                startDate={startDate} setStartDate={setStartDate}
                endDate={endDate} setEndDate={setEndDate}
                exportToPDF={exportToPDF} exportToExcel={exportToExcel}
                allLogs={logsForUser} registeredEmployees={registeredEmployees}
              />
            )}

            {activeTab === 'megelin-logs' && showMegelinLogs && (
              <AttendanceLogs 
                account={ACCOUNT_MEGELIN}
                startDate={startDate} setStartDate={setStartDate}
                endDate={endDate} setEndDate={setEndDate}
                exportToPDF={exportToPDF} exportToExcel={exportToExcel}
                allLogs={logsForUser} registeredEmployees={registeredEmployees}
              />
            )}

            {activeTab === 'register' && (
              <RegisterEmployee 
                handleRegisterEmployee={handleRegisterEmployee} photoBase64={photoBase64} fileInputRef={fileInputRef}
                handleFileChange={handleFileChange} newEmpId={newEmpId} setNewEmpId={setNewEmpId} newEmpEID={newEmpEID}
                setNewEmpEID={setNewEmpEID} newEmpName={newEmpName} setNewEmpName={setNewEmpName} newEmpDateHired={newEmpDateHired}
                setNewEmpDateHired={setNewEmpDateHired} newEmpPosition={newEmpPosition} setNewEmpPosition={setNewEmpPosition}
                newEmpShift={newEmpShift} setNewEmpShift={setNewEmpShift}
                newEmpAccount={newEmpAccount} setNewEmpAccount={setNewEmpAccount}
                error={error} successMsg={successMsg}
              />
            )}

            {activeTab === 'employees' && (
              <EmployeeList 
                searchDept={searchDept} setSearchDept={setSearchDept} filterShift={filterShift} setFilterShift={setFilterShift}
                registeredEmployees={displayedEmployees}
                editingId={editingId} setEditingId={setEditingId}
                editName={editName} setEditName={setEditName}
                editPosition={editPosition} setEditPosition={setEditPosition}
                editShift={editShift} setEditShift={setEditShift}
                handleUpdateEmployee={handleUpdateEmployee} startEdit={startEdit}
                handleDeleteEmployee={handleDeleteEmployee}
                isAdmin={isAdminUser(user)}
                canManageActions={canManageEmployeeActions(user)}
                onRefreshEmployees={fetchData}
              />
            )}

            {activeTab === 'user-management' && user?.role === 'admin' && (
              <UserManagement />
            )}

            {activeTab === 'tl-accounts' && user?.role === 'admin' && (
              <TLAccounts />
            )}

            {activeTab === 'employee-portal-accounts' && user?.role === 'admin' && (
              <EmployeePortalAccounts
                registeredEmployees={registeredEmployees}
                onRefreshEmployees={fetchData}
              />
            )}
            
          </div>
        </main>
      </div>
      <button className="theme-toggle-btn" onClick={() => setIsDarkMode(!isDarkMode)}>
        {isDarkMode ? '☀︎' : '⏾'}
      </button>
    </div>
  );
}

export default App;